import {
  Import,
  Infinite,
  Mail,
  Page,
  SmartphoneDevice,
  SoundHigh,
} from 'iconoir-react';
import type { ReactNode } from 'react';
import { cn } from '@listening/utils/react-helpers';
import {
  CitationRulesMockup,
  DevicesMockup,
  NewsletterInboxMockup,
  PronunciationMockup,
  QuotaMockup,
  ZoteroLibraryMockup,
} from './bento-mockups';
import { SectionHeading } from './section-heading';
import * as m from '@/paraglide/messages';

function CardText({
  icon,
  label,
  title,
  children,
}: {
  icon: ReactNode;
  label: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <div>
      <p className='flex items-center gap-2 font-mono text-[11px] font-medium tracking-[0.12em] text-(--v2-chip-text) uppercase'>
        <span className='text-azure-595'>{icon}</span>
        {label}
      </p>
      <h3 className='mt-3 text-lg leading-tight font-semibold tracking-[-0.03em] text-balance text-(--v2-text-primary) sm:text-[22px]'>
        {title}
      </h3>
      <p className='mt-1.5 text-sm leading-relaxed text-pretty text-(--v2-chip-text) sm:mt-2 sm:text-[15px]'>
        {children}
      </p>
    </div>
  );
}

function BentoCard({
  className,
  children,
}: {
  className: string;
  children: ReactNode;
}) {
  return (
    <article
      className={cn(
        'flex overflow-hidden rounded-3xl bg-(--v2-app-bg) p-4 ring-1 ring-black/[0.05] sm:p-6',
        className,
      )}
    >
      {children}
    </article>
  );
}

export function BentoSection() {
  return (
    <section
      aria-labelledby='why-bento-heading'
      className='mx-auto max-w-6xl px-5 pt-4 pb-14 sm:px-6 sm:pt-8 sm:pb-24 lg:pt-14 lg:pb-28'
    >
      <SectionHeading
        description={m.why_listening_bento_description()}
        id='why-bento-heading'
        title={m.why_listening_bento_title()}
      />
      <div className='mt-10 grid gap-2.5 sm:mt-14 sm:grid-cols-2 lg:grid-cols-6'>
        <BentoCard className='flex-col gap-4 sm:col-span-2 sm:gap-5 md:grid md:grid-cols-[1fr_1.15fr] md:items-stretch md:gap-8 lg:col-span-4'>
          <div className='md:self-center'>
            <CardText
              icon={<Page className='size-3.5' />}
              label={m.why_listening_citations_label()}
              title={m.why_listening_citations_title()}
            >
              {m.why_listening_citations_body()}
            </CardText>
          </div>
          <div aria-hidden='true' className='flex'>
            <CitationRulesMockup />
          </div>
        </BentoCard>
        <BentoCard className='flex-col-reverse gap-4 sm:gap-5 lg:col-span-2'>
          <CardText
            icon={<SoundHigh className='size-3.5' />}
            label={m.why_listening_pronunciation_label()}
            title={m.why_listening_pronunciation_title()}
          >
            {m.why_listening_pronunciation_body()}
          </CardText>
          <div aria-hidden='true' className='flex flex-1'>
            <PronunciationMockup />
          </div>
        </BentoCard>
        <BentoCard className='flex-col gap-4 sm:gap-5 lg:col-span-2'>
          <CardText
            icon={<Import className='size-3.5' />}
            label={m.why_listening_zotero_label()}
            title={m.why_listening_zotero_title()}
          >
            {m.why_listening_zotero_body()}
          </CardText>
          <div
            aria-hidden='true'
            className='-mx-4 -mb-4 flex flex-1 sm:-mx-6 sm:-mb-6'
          >
            <ZoteroLibraryMockup />
          </div>
        </BentoCard>
        <BentoCard className='hidden flex-col-reverse gap-4 sm:gap-5 lg:col-span-2 lg:flex'>
          <CardText
            icon={<Mail className='size-3.5' />}
            label={m.why_listening_newsletter_label()}
            title={m.why_listening_newsletter_title()}
          >
            {m.why_listening_newsletter_body()}
          </CardText>
          <div aria-hidden='true' className='flex flex-1'>
            <NewsletterInboxMockup />
          </div>
        </BentoCard>
        <BentoCard className='flex-col gap-4 sm:col-span-2 sm:gap-5 lg:col-span-2'>
          <CardText
            icon={<Infinite className='size-3.5' />}
            label={m.why_listening_quota_label()}
            title={m.why_listening_quota_title()}
          >
            {m.why_listening_quota_body()}
          </CardText>
          <div aria-hidden='true' className='flex flex-1'>
            <QuotaMockup />
          </div>
        </BentoCard>
        <BentoCard className='flex-col gap-4 sm:col-span-2 sm:gap-5 md:grid md:grid-cols-[1fr_1.2fr] md:items-stretch md:gap-8 lg:col-span-6'>
          <div className='md:self-center'>
            <CardText
              icon={<SmartphoneDevice className='size-3.5' />}
              label={m.why_listening_devices_label()}
              title={m.why_listening_devices_title()}
            >
              {m.why_listening_devices_body()}
            </CardText>
          </div>
          <div aria-hidden='true' className='flex'>
            <DevicesMockup />
          </div>
        </BentoCard>
      </div>
    </section>
  );
}
